// Vercel Skew Protection pin. Vercel routes any request carrying `__vdpl=<deployment id>` to that
// deployment, so an old tab keeps fetching the chunks that were built WITH its HTML instead of
// asking the newest deploy for files that no longer exist.
// Scoped to `path=/assets` on purpose: a site-wide cookie also pins full-page loads and
// `/meta.json`, which would trap users on an old version forever. With this scope the HTML and the
// version check always see the newest deploy, and only the hashed chunks are pinned.
//
// Cookies are shared across tabs, but each tab knows its OWN deployment id (baked in at build
// time), so every tab re-asserts its pin whenever it is the one doing the work: on boot, on every
// in-tab navigation (before Vue Router fetches the route's chunk), and whenever the tab becomes
// active again (focus / visibility / bfcache restore). The FIRST write happens in index.html's
// pre-boot script, before any asset request — see the comment there for why that matters. The cookie therefore almost always names
// the deploy of the tab the user is actually using. The one gap left is a BACKGROUND tab fetching
// a chunk while another tab on a newer build is focused — rare, and it degrades to the same single
// clean reload as before skew protection, never worse.
//
// Reads the build-time define directly: importing @Constants here would close an import cycle
// (@Constants -> ... -> @Libraries) and throw a TDZ error at boot.
const PIN_COOKIE_NAME = '__vdpl';
const PIN_COOKIE_MAX_AGE = 60 * 60 * 24 * 7; // 7 days — match the dashboard "Maximum Age"
const DEPLOYMENT_ID = __AF_DEPLOYMENT_ID__;

let isListening = false;

// Safe to call repeatedly: rewriting the same cookie only refreshes its max-age.
function pinAssetsToDeployment(): void {
  if (!DEPLOYMENT_ID) return;

  document.cookie = `${PIN_COOKIE_NAME}=${DEPLOYMENT_ID}; path=/assets; max-age=${PIN_COOKIE_MAX_AGE}; secure; samesite=lax`;
}

function onTabVisible(): void {
  if (document.visibilityState === 'visible') pinAssetsToDeployment();
}

// Boot-time entry: pin now and keep re-asserting whenever this tab becomes the active one.
export default function startAssetPinning(): void {
  if (!DEPLOYMENT_ID) return;

  pinAssetsToDeployment();

  if (isListening) return;
  isListening = true;

  window.addEventListener('focus', pinAssetsToDeployment);
  window.addEventListener('pageshow', pinAssetsToDeployment);
  document.addEventListener('visibilitychange', onTabVisible);
}

export { pinAssetsToDeployment };
